// v19.30 — Buyer's personal library of purchased beats.
// Route: /my-beats
// Requires auth. Lists every purchase belonging to the user with
// re-download links + license PDF.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DownloadSimple, FileText, ArrowLeft, MusicNote } from "@phosphor-icons/react";
import GlobalNav from "../components/GlobalNav";
import API from "../api";
import { useAuth } from "../auth/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyBeatsPage() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!ready) return;
    if (!user) { nav("/"); return; }
    (async () => {
      try {
        const { data } = await API.get("/store/purchases/mine");
        setPurchases(data.purchases || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not load library");
      } finally { setLoading(false); }
    })();
  }, [ready, user, nav]);

  if (!ready || loading) return <div className="min-h-screen bg-black text-white flex items-center justify-center">Loading your library…</div>;

  return (
    <div className="min-h-screen text-white" style={{ background: "radial-gradient(circle at 50% 0%, rgba(34,197,94,0.05), #000 60%)" }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between gap-3 mb-6">
          <button onClick={() => nav("/beats")} className="text-white/50 hover:text-white text-sm flex items-center gap-1" data-testid="my-beats-back">
            <ArrowLeft size={16} weight="bold" /> Back to store
          </button>
          <GlobalNav />
        </div>
        <h1 className="font-display font-bold text-4xl mb-6">My Beats</h1>

        {err ? <div className="text-red-400 text-sm">{err}</div> : purchases.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-white/60">
            No purchases yet. Head over to <button onClick={() => nav("/beats")} className="underline hover:text-emerald-300">the store</button> to grab a beat.
          </div>
        ) : (
          <div className="space-y-3">
            {purchases.map((p) => (
              <PurchaseRow key={p.id} p={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PurchaseRow({ p }) {
  const [details, setDetails] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    if (details) { setDetails(null); return; }
    setBusy(true);
    try {
      const { data } = await API.get(`/store/purchases/${p.id}?token=${encodeURIComponent(p.download_token)}`);
      setDetails(data);
    } catch (e) {
      toast.error("Could not load downloads — token expired?");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-[#0a0a0a] border border-[#1f1f1f] p-4" data-testid={`my-purchase-${p.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono font-bold text-lg">"{p.beat_name}"</div>
          <div className="text-xs text-white/50">{p.artist_tag} · {p.tier_label} · ${((p.price_cents || 0) / 100).toFixed(0)}</div>
          <div className="text-[11px] text-white/40 mt-1 flex items-center gap-1"><MusicNote size={11} weight="bold" /> {p.created_at?.slice(0, 10)} · links expire {p.download_expires_at?.slice(0, 10)}</div>
        </div>
        <button onClick={load} disabled={busy} className="px-3 py-1.5 rounded-full text-xs font-bold bg-white/5 border border-white/10 hover:border-emerald-400/40 disabled:opacity-50" data-testid={`my-purchase-toggle-${p.id}`}>{busy ? "…" : details ? "Hide downloads" : "Show downloads"}</button>
      </div>
      {details ? (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {details.downloads.map((d) => (
            <a key={d.kind} href={`${API_URL}${d.url}`} download className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-bold ${d.kind === "license" ? "border-white/10 hover:border-emerald-400/40" : "border-emerald-400/40 bg-emerald-500/10 hover:bg-emerald-500/20"}`} data-testid={`my-download-${p.id}-${d.kind}`}>
              {d.kind === "license" ? <FileText size={14} weight="bold" /> : <DownloadSimple size={14} weight="bold" />} {d.label}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}
